// Construction mode on the city view (the live game's hammer): the mode pill, the queue count under the hammer with its
// drop-down queue (speed up the job building now, cancel any), and the pop-up that sits above a tapped building with its
// next level's cost and time, UPGRADE TO LEVEL N, and SLIDE TO DOWNGRADE (City Hall 15+, as the game gates it).
import { useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent as RPointerEvent } from "react";
import { api } from "@/api/client";
import type { BuildingDef } from "@/api/types";
import { art } from "@/kit/art";
import { CostLine, Countdown } from "@/kit/city";
import { IconChevronRight, IconClose, IconInfo } from "@/kit/icons";
import { Confirm } from "@/kit/overlays";
import { Button, Disc, Medallion, cx } from "@/kit/primitives";
import { useNow } from "@/state/clock";
import { BUILDING_NAMES, DEMOLISH_MIN_HQ, type BuildingKey } from "@/game/constants";
import { buildBlock, buildingCost, isMaxed, nextLevel, queuedLevels, queueProgress, shortfall, type QueueEntry } from "@/game/city";
import { fmtDuration, fmtWhen, parseHMS } from "@/game/time";
import { useCity } from "./CityRoute";
import { SpeedupSheet } from "./SpeedupSheet";

/** Where the pop-up points, in px inside the camera's window (it follows the camera): the arrow's x, the point above the building (preferred) and the point
 * below its plate (used when the pop-up doesn't fit above — the City Hall at the top of the painting). */
export interface PopAnchor {
  x: number;
  above: number;
  below: number;
}

/** Ready / blocked / maxed for a plate's hammer chip in construction mode. */
export function plateReadiness(v: Parameters<typeof buildBlock>[0], b: BuildingDef | undefined, queueFull: boolean): "ready" | "blocked" | "max" | null {
  if (!b) return null;
  if (isMaxed(b)) return "max";
  return buildBlock(v, b, { queueFull }) ? "blocked" : "ready";
}

// ------------------------------------------------------------------ the pop-up above a building

export function BuildPopover({ bkey, anchor, stageW, queue, slots, onClose, onInfo }: {
  bkey: BuildingKey; anchor: PopAnchor; stageW: number; queue: QueueEntry[]; slots: number; onClose: () => void; onInfo?: () => void;
}) {
  const { v, wid, vid, actions } = useCity();
  const now = useNow();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; arrow: number; flip: boolean } | null>(null);

  // place it: centred on the building, kept inside the stage, above unless there's no room
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const gap = 12;
    const left = Math.max(8, Math.min(stageW - w - 8, anchor.x - w / 2));
    const flip = anchor.above - h - gap < 6;
    const top = flip ? anchor.below + gap : anchor.above - h - gap;
    const arrow = Math.max(18, Math.min(w - 18, anchor.x - left));
    if (!pos || pos.left !== left || pos.top !== top || pos.arrow !== arrow || pos.flip !== flip) setPos({ left, top, arrow, flip });
  });

  // (bringing it on screen is the city camera's job: CityView's FollowPop eases the camera so the building and its pop-up fit)

  const b = v?.buildings?.find((x) => x.key === bkey);
  if (!v || !b) return null;

  const name = BUILDING_NAMES[bkey];
  const maxed = isMaxed(b);
  const next = nextLevel(b);
  const cost = buildingCost(b);
  const s = shortfall(v, cost);
  const queued = queuedLevels(v, bkey);
  const queueFull = queue.length >= slots;
  const slotOpensInMs = queue[0] ? queue[0].endMs - now : undefined;
  const reason = buildBlock(v, b, { queueFull, slotOpensInMs });
  const downTo = Number(b.next_level_demolish);
  const canDown = b.level > 0 || queued > 0;
  const downReason = !v.can_demolish ? `Downgrade at City Hall ${DEMOLISH_MIN_HQ}` : buildBlock(v, b, { queueFull, slotOpensInMs, demolish: true });
  const upKey = `cm-expand:${bkey}`;
  const downKey = `cm-demolish:${bkey}`;

  const style: CSSProperties = pos
    ? { left: pos.left, top: pos.top, ["--arrow" as string]: `${pos.arrow}px` }
    : { left: 0, top: 0, visibility: "hidden" };

  return (
    <div ref={ref} className={cx("cm-pop", pos?.flip && "flip")} style={style} role="dialog" aria-label={`${name} construction`}>
      <div className="cm-pop-head">
        <Medallion src={art.med(bkey)} alt="" size={58} dim={b.level === 0} />
        <div className="cm-pop-title">
          <h3>{name} <span className="gold">{b.level > 0 ? `L${b.level}` : "· not built"}</span></h3>
          {queued > 0 && <div className="cm-pop-sub">{queued === 1 ? `Level ${b.level + 1} in the queue` : `${queued} levels in the queue`}</div>}
        </div>
        {onInfo && <Disc size="xs" label={`Open ${name}`} onClick={onInfo}><IconInfo /></Disc>}
        <Disc size="xs" label="Close" onClick={onClose}><IconClose /></Disc>
      </div>

      {maxed ? (
        <div className="cm-pop-max">Final level reached</div>
      ) : (
        <>
          <CostLine cost={cost} pop={cost.p_u} time={cost.time} short={s.short} className="cm-pop-cost" />
          <Button
            kind="crimson"
            wide
            disabled={!!reason}
            busy={actions.isBusy(upKey)}
            onClick={() => actions.run(upKey, () => api.village.expand({ wid, vid }, bkey), { success: `${name} level ${next} added to the queue.` })}
          >
            {b.level > 0 || queued > 0 ? `Upgrade to Level ${next}` : "Build Level 1"}
          </Button>
          {reason && <div className="c-note">{reason}</div>}
        </>
      )}

      {canDown && (
        <SlideToConfirm
          label={downReason ?? `Slide to downgrade · L${downTo} · ${fmtDuration(parseHMS(b.time_demolish))}`}
          disabled={!!downReason}
          busy={actions.isBusy(downKey)}
          onConfirm={() => actions.run(downKey, () => api.village.demolish({ wid, vid }, bkey), { success: `${name} downgrade to level ${downTo} queued.` })}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ slide to confirm (the live game's "Slide to Downgrade")

export function SlideToConfirm({ label, onConfirm, disabled, busy }: { label: string; onConfirm: () => unknown; disabled?: boolean; busy?: boolean }) {
  const track = useRef<HTMLDivElement>(null);
  const knob = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ x0: number; max: number } | null>(null);
  const [off, setOff] = useState(0);
  const [max, setMax] = useState(1);
  const [dragging, setDragging] = useState(false);

  const measure = () => {
    const t = track.current;
    const k = knob.current;
    return t && k ? Math.max(1, t.clientWidth - k.offsetWidth - 8) : 1;
  };
  const finish = (o: number, m: number) => {
    setDragging(false);
    drag.current = null;
    if (o >= m * 0.9 && !disabled && !busy) {
      setOff(m);
      void Promise.resolve(onConfirm()).finally(() => setOff(0));
    } else setOff(0);
  };

  const down = (e: RPointerEvent<HTMLButtonElement>) => {
    if (disabled || busy) return;
    const m = measure();
    setMax(m);
    drag.current = { x0: e.clientX - off, max: m };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e: RPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    setOff(Math.max(0, Math.min(d.max, e.clientX - d.x0)));
  };
  const up = () => {
    const d = drag.current;
    if (d) finish(off, d.max);
  };
  const key = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled || busy) return;
    if (e.key === "ArrowRight" || e.key === "End") {
      e.preventDefault();
      const m = measure();
      setMax(m);
      const o = e.key === "End" ? m : Math.min(m, off + m / 4);
      if (o >= m * 0.9) finish(m, m);
      else setOff(o);
    } else if (e.key === "ArrowLeft" || e.key === "Home") {
      e.preventDefault();
      setOff(0);
    }
  };

  const pct = Math.round((off / max) * 100);
  return (
    <div ref={track} className={cx("cm-slide", disabled && "disabled", dragging && "dragging", busy && "busy")} style={{ ["--off" as string]: `${off}px`, ["--p" as string]: (off / max).toFixed(2) }}>
      <i className="fill" aria-hidden="true" />
      <span className="lbl">{busy ? "Queuing…" : label}</span>
      <button
        ref={knob}
        type="button"
        className="knob"
        role="slider"
        aria-label={disabled ? label : "Slide to downgrade"}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={() => { drag.current = null; setDragging(false); setOff(0); }}
        onKeyDown={key}
      >
        <IconChevronRight />
      </button>
    </div>
  );
}

// ------------------------------------------------------------------ the queue under the hammer

export function QueuePanel({ queue, slots, onClose, onHall }: { queue: QueueEntry[]; slots: number; onClose: () => void; onHall: () => void }) {
  const { wid, vid, actions } = useCity();
  const now = useNow();
  const [speedup, setSpeedup] = useState<QueueEntry | null>(null);
  const [cancel, setCancel] = useState<QueueEntry | null>(null);
  const end = queue.length ? queue[queue.length - 1].endMs : 0;
  return (
    <div className="cm-queue" role="dialog" aria-label="Construction queue">
      {queue.length === 0 ? (
        <div className="cm-q-empty">Nothing under construction — tap a building in construction mode to add one.</div>
      ) : (
        queue.map((q, i) => {
          const first = i === 0;
          const name = BUILDING_NAMES[q.item.key] ?? q.item.name;
          return (
            <div key={`${q.item.key}-${q.item.level}-${i}`} className={cx("cm-q-row", first && "first", !q.item.is_upgrade && "down")}>
              {first && <i className="fill" style={{ width: `${queueProgress(q, now) * 100}%` }} aria-hidden="true" />}
              <Disc size="xs" label={`Cancel ${name} level ${q.item.level}`} onClick={() => setCancel(q)}><IconClose /></Disc>
              <span className="nm">{name} Level {q.item.level}{!q.item.is_upgrade && <em> · downgrade</em>}</span>
              {first && (
                <Button kind="gold" size="sm" onClick={() => setSpeedup(q)} disabled={!!q.item.has_used_speedup} title={q.item.has_used_speedup ? "Already sped up" : undefined}>
                  Speed up
                </Button>
              )}
              <span className="t">{first ? <Countdown endMs={q.endMs} /> : fmtDuration(q.durationMs)}</span>
            </div>
          );
        })
      )}
      <div className="cm-q-foot">
        <span>{queue.length} of {slots} slot{slots === 1 ? "" : "s"}{queue.length ? <> · clear at {fmtWhen(end, now)}</> : null}</span>
        <button type="button" className="link caps" onClick={onHall}>City overview <IconChevronRight /></button>
        <Disc size="xs" label="Close the queue" onClick={onClose}><IconClose /></Disc>
      </div>
      <SpeedupSheet job={speedup} onClose={() => setSpeedup(null)} />
      <Confirm
        open={!!cancel}
        onClose={() => setCancel(null)}
        title="Cancel this job?"
        text={cancel ? `${BUILDING_NAMES[cancel.item.key] ?? cancel.item.name} level ${cancel.item.level} comes out of the queue and its resources come back. Jobs after it move up.` : ""}
        action="Cancel the job"
        busy={actions.isBusy("cancel_expand")}
        onConfirm={async () => {
          if (!cancel) return;
          const r = await actions.run("cancel_expand", () => api.village.cancelExpand({ wid, vid }, cancel.index));
          if (r) setCancel(null);
        }}
      />
    </div>
  );
}
